import { Injectable } from '@angular/core';
import { CellFilter, ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableFilters, MatchMode } from 'src/app/services/models/dynamic-table.models';
import { DatePickerValue, RelativeDateOption } from 'src/app/common/bhub-common-module/components/date-picker/date-picker.component';

@Injectable({
  providedIn: 'root'
})
export class CellFilterService {

  /**
   * Match modes whose values list behaves as a set (SQL IN / NOT IN), so a
   * multi-row source can contribute one value per row. Comparison modes
   * (greater_than, between, ...) have no sensible OR-of-values meaning, so they
   * always collapse to a single value.
   */
  private supportsMultipleValues(matchMode?: string): boolean {
    const mode = matchMode || 'exact';
    return mode === 'exact' || mode === 'not_equal';
  }

  /**
   * Pull `fieldName` off every row, flattening array-valued fields, dropping
   * null/undefined and de-duplicating while preserving the original value types
   * (so numeric IDs stay numeric).
   */
  private collectFieldValues(rows: any[], fieldName: string): any[] {
    const values: any[] = [];
    const seen = new Set<string>();

    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const raw = row[fieldName];
      if (raw === null || raw === undefined) continue;

      const candidates = Array.isArray(raw) ? raw : [raw];
      for (const value of candidates) {
        if (value === null || value === undefined) continue;
        const key = `${typeof value}:${String(value)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        values.push(value);
      }
    }

    return values;
  }

  /** Normalize a row / array-of-rows / null argument into a plain array of rows. */
  private toRowArray(selectedRows: any): any[] {
    if (!selectedRows) return [];
    return Array.isArray(selectedRows) ? selectedRows.filter(r => r !== null && r !== undefined) : [selectedRows];
  }

  /**
   * Resolve a `contextKey.fieldName` placeholder — the form stored inside saved
   * contextInstructions filters — into concrete filter values.
   *
   * An array-shaped context (e.g. a Multi-Select selection) contributes one value
   * per element so the filter matches ANY of them. Callers fetching a single record,
   * or using a comparison match mode, pass multiValue:false to keep the historical
   * first-element-only behavior.
   */
  resolveContextReferenceValues(
    contextData: { [key: string]: any } | undefined,
    contextKey: string,
    fieldName: string,
    options?: { multiValue?: boolean; matchMode?: string }
  ): any[] {
    const contextObj = contextData?.[contextKey];
    if (contextObj === null || contextObj === undefined) {
      return [];
    }

    const expand = options?.multiValue !== false && this.supportsMultipleValues(options?.matchMode);

    if (Array.isArray(contextObj)) {
      const rows = expand ? contextObj : contextObj.slice(0, 1);
      return this.collectFieldValues(rows, fieldName);
    }

    if (typeof contextObj === 'object') {
      return this.collectFieldValues([contextObj], fieldName);
    }

    return [];
  }

  /**
   * Build filters for dynamic table service from cell configuration
   * @param cellFilters Array of cell filters
   * @param viewManager View manager containing context and variables
   * @param selectedRows Optional selected row — or rows — for the 'firstSelectedRow' filter type.
   *                     Multi-Select passes every selected row so the filter matches ANY of them.
   * @param options.multiValue When false, array-shaped sources collapse to their first element
   *                     (the pre-multi-select behavior). Callers fetching a SINGLE record pass
   *                     false, because `IN (...)` with `limit 1` would return an arbitrary row.
   * @returns DynamicTableFilters object or undefined if no filters
   */
  buildCellFilters(
    cellFilters: CellFilter[] | undefined,
    viewManager: ViewManager | null,
    selectedRows?: any,
    options?: { multiValue?: boolean }
  ): DynamicTableFilters | undefined {
    if (!cellFilters || cellFilters.length === 0) {
      return undefined;
    }

    const filters: DynamicTableFilters = {};
    const rows = this.toRowArray(selectedRows);
    const allowMultiValue = options?.multiValue !== false;

    for (const cellFilter of cellFilters) {
      // Skip disabled filters
      if (cellFilter.enabled === false) {
        continue;
      }

      // A multi-row source only widens into `IN (...)` for set-semantics match modes.
      const expandToAllRows = allowMultiValue && this.supportsMultipleValues(cellFilter.matchMode);
      let filterValues: any[] = [];

      switch (cellFilter.filterType) {
        case 'static':
          // Use static values, resolving any DatePickerValue objects to ISO strings
          if (cellFilter.staticValues && cellFilter.staticValues.length > 0) {
            filterValues = cellFilter.staticValues.map(v => this.resolveStaticValue(v));
          }
          break;

        case 'context':
          // Get values from context object and field
          if (cellFilter.contextObject && cellFilter.contextField && viewManager?.contextData) {
            const contextObj = viewManager.contextData[cellFilter.contextObject];

            if (Array.isArray(contextObj)) {
              // An array context (e.g. a Multi-Select's selection) contributes the
              // field from EVERY element, so the filter matches any of them. When
              // multi-value isn't allowed, fall back to the first element only.
              const sourceRows = expandToAllRows ? contextObj : contextObj.slice(0, 1);
              filterValues = this.collectFieldValues(sourceRows, cellFilter.contextField);
            } else if (contextObj && typeof contextObj === 'object') {
              const contextValue = contextObj[cellFilter.contextField];
              if (contextValue !== null && contextValue !== undefined) {
                filterValues = Array.isArray(contextValue) ? contextValue : [contextValue];
              }
            }
          }
          break;

        case 'firstSelectedRow':
          // Get the value from the selected row(s). Single-selection callers pass one
          // row; Multi-Select passes all selected rows so the filter matches any of them.
          if (cellFilter.sourceField && rows.length > 0) {
            const sourceRows = expandToAllRows ? rows : rows.slice(0, 1);
            filterValues = this.collectFieldValues(sourceRows, cellFilter.sourceField);
          }
          break;
      }

      // Only add filter if we have values
      if (filterValues.length > 0) {
        this.addFilterCriteria(filters, cellFilter.fieldId, filterValues, cellFilter.matchMode || 'exact');
      }
    }

    // Return undefined if no filters were actually built
    return Object.keys(filters).length > 0 ? filters : undefined;
  }


  /**
   * Build the `filters` map for a saved context instruction: the same filters a cell
   * would send, but with `contextKey.fieldName` placeholders left unresolved, since the
   * instruction is evaluated later (resolveContextFilters does that at fetch time).
   *
   * Carries each filter's match mode, and merges two bounds on one field into a single
   * 'between' - DynamicTableFilters holds one criteria per field, so a lower and an upper
   * bound can only travel together that way.
   *
   * @param cellFilters The configured filters
   * @param legacy Optional pre-cellFilters pairing (filterFieldId + sourceContext.sourceField)
   * @returns The filters map, or undefined when nothing resolved to a filter
   */
  buildContextInstructionFilters(
    cellFilters: CellFilter[] | undefined,
    legacy?: { fieldId?: number; sourceContextVariable?: string; sourceField?: string }
  ): DynamicTableFilters | undefined {
    const filters: DynamicTableFilters = {};

    if (legacy?.fieldId && legacy.sourceContextVariable && legacy.sourceField) {
      filters[legacy.fieldId] = {
        values: [`${legacy.sourceContextVariable}.${legacy.sourceField}`],
        matchMode: 'exact'
      };
    }

    for (const cellFilter of cellFilters || []) {
      if (cellFilter.enabled === false) {
        continue;
      }

      let values: any[] = [];

      if (cellFilter.filterType === 'static' && cellFilter.staticValues && cellFilter.staticValues.length > 0) {
        // A static date is stored as a DatePickerValue, so resolve it the same way a
        // cell's own filters do rather than sending the picker object itself
        values = cellFilter.staticValues.map(value => this.resolveStaticValue(value));
      } else if (cellFilter.filterType === 'context' && cellFilter.contextObject && cellFilter.contextField) {
        // Resolved at fetch time, once the referenced context has data
        values = [`${cellFilter.contextObject}.${cellFilter.contextField}`];
      }

      if (values.length === 0) {
        continue;
      }

      this.addFilterCriteria(filters, cellFilter.fieldId, values, cellFilter.matchMode || 'exact');
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }

  /**
   * Assemble a criteria from the values each placeholder in a stored instruction filter
   * resolved to, one array per placeholder.
   *
   * A 'between' carries its two bounds as two placeholders. If only one of them resolves
   * - an end date left blank, say - it degrades to the one-sided comparison that
   * survives, because BETWEEN needs both ends and the API rejects a one-value between.
   */
  buildResolvedCriteria(resolvedPerPlaceholder: any[][], matchMode?: string): { values: any[]; matchMode?: string } {
    const isPresent = (value: any) => value !== null && value !== undefined;
    const flattened = resolvedPerPlaceholder.reduce<any[]>((all, values) => all.concat(values), []).filter(isPresent);

    if (matchMode !== 'between' || resolvedPerPlaceholder.length < 2) {
      return { values: flattened, matchMode };
    }

    const lower = resolvedPerPlaceholder[0]?.[0];
    const upper = resolvedPerPlaceholder[1]?.[0];

    if (isPresent(lower) && isPresent(upper)) {
      return { values: [lower, upper], matchMode: 'between' };
    }
    if (isPresent(lower)) {
      return { values: [lower], matchMode: 'greater_than_or_equal' };
    }
    if (isPresent(upper)) {
      return { values: [upper], matchMode: 'less_than_or_equal' };
    }

    return { values: [], matchMode };
  }

  /**
   * Add one field's criteria to a filters map, folding a second bound on the same field
   * into a 'between' instead of overwriting what is already there.
   */
  private addFilterCriteria(filters: DynamicTableFilters, fieldId: number, values: any[], matchMode: MatchMode): void {
    const existing = filters[fieldId];

    if (existing && this.canMergeIntoBetween(existing.matchMode, matchMode)) {
      filters[fieldId] = {
        values: this.buildBetweenValues(existing, { values, matchMode }),
        matchMode: 'between'
      };
      return;
    }

    filters[fieldId] = { values, matchMode };
  }

  private readonly relativeDateOptions: RelativeDateOption[] = [
    { label: 'Today', value: 'today', days: 0 },
    { label: 'Yesterday', value: 'yesterday', days: -1 },
    { label: 'Tomorrow', value: 'tomorrow', days: 1 },
    { label: 'Last 7 days', value: 'last7days', days: -7 },
    { label: 'Last 30 days', value: 'last30days', days: -30 },
    { label: 'Last 90 days', value: 'last90days', days: -90 },
    { label: 'Last year', value: 'lastyear', years: -1 },
    { label: 'Next 7 days', value: 'next7days', days: 7 },
    { label: 'Next 30 days', value: 'next30days', days: 30 },
    { label: 'Next 90 days', value: 'next90days', days: 90 },
    { label: 'Next year', value: 'nextyear', years: 1 },
  ];

  /**
   * Resolve a static value — if it's a DatePickerValue, calculate the actual date.
   * Otherwise, return as-is.
   */
  private resolveStaticValue(value: any): any {
    if (value && typeof value === 'object' && value.mode) {
      return this.resolveDatePickerValue(value as DatePickerValue);
    }
    return value;
  }

  /**
   * Resolve a DatePickerValue to an ISO date string for filtering.
   */
  private resolveDatePickerValue(dpValue: DatePickerValue): string {
    let date: Date;

    if (dpValue.mode === 'specific') {
      date = dpValue.value instanceof Date ? dpValue.value : new Date(dpValue.value);
    } else {
      // Relative mode
      date = new Date();

      if (dpValue.value === 'custom') {
        if (dpValue.customDays) {
          date.setDate(date.getDate() + dpValue.customDays);
        }
        if (dpValue.customMonths) {
          date.setMonth(date.getMonth() + dpValue.customMonths);
        }
        if (dpValue.customYears) {
          date.setFullYear(date.getFullYear() + dpValue.customYears);
        }
      } else {
        const option = this.relativeDateOptions.find(opt => opt.value === dpValue.value);
        if (option) {
          if (option.days) {
            date.setDate(date.getDate() + option.days);
          }
          if (option.months) {
            date.setMonth(date.getMonth() + option.months);
          }
          if (option.years) {
            date.setFullYear(date.getFullYear() + option.years);
          }
        }
      }
    }

    // Format as ISO string without time
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  }

  /**
   * Check if two match modes on the same field can be merged into a 'between' filter.
   * e.g. greater_than + less_than, greater_than_or_equal + less_than_or_equal, etc.
   */
  private canMergeIntoBetween(existingMode: string | undefined, newMode: string): boolean {
    const lowerBounds = ['greater_than', 'greater_than_or_equal'];
    const upperBounds = ['less_than', 'less_than_or_equal'];
    return (lowerBounds.includes(existingMode || '') && upperBounds.includes(newMode)) ||
           (upperBounds.includes(existingMode || '') && lowerBounds.includes(newMode));
  }

  /**
   * Build a [min, max] values array from two range filters on the same field.
   */
  private buildBetweenValues(
    existing: { values: any[]; matchMode?: string },
    incoming: { values: any[]; matchMode: string }
  ): any[] {
    const lowerBounds = ['greater_than', 'greater_than_or_equal'];
    const lower = lowerBounds.includes(existing.matchMode || '') ? existing.values[0] : incoming.values[0];
    const upper = lowerBounds.includes(existing.matchMode || '') ? incoming.values[0] : existing.values[0];
    return [lower, upper];
  }

  /**
   * Check if cell has any active filters configured
   * @param cellFilters Array of cell filters
   * @returns true if has active filters
   */
  hasCellFilters(cellFilters: CellFilter[] | undefined): boolean {
    return cellFilters && cellFilters.length > 0 && cellFilters.some(f => f.enabled !== false);
  }

  /**
   * Check if any configured dynamic filters (context or firstSelectedRow) failed to resolve a value.
   * Returns true if at least one enabled dynamic filter produced no value, meaning the data
   * should not be loaded (to avoid returning unfiltered results).
   */
  hasUnresolvedFilters(
    cellFilters: CellFilter[] | undefined,
    viewManager: ViewManager | null,
    selectedRows?: any
  ): boolean {
    if (!cellFilters || cellFilters.length === 0) {
      return false; // No filters configured — nothing to check
    }

    const rows = this.toRowArray(selectedRows);

    for (const cellFilter of cellFilters) {
      if (cellFilter.enabled === false) {
        continue;
      }

      // Only check dynamic filter types that depend on runtime values.
      // A filter counts as resolved when ANY source row yields a value — matching
      // what buildCellFilters would actually send.
      if (cellFilter.filterType === 'context') {
        let resolved = false;
        if (cellFilter.contextObject && cellFilter.contextField && viewManager?.contextData) {
          const contextObj = viewManager.contextData[cellFilter.contextObject];
          if (Array.isArray(contextObj)) {
            resolved = this.collectFieldValues(contextObj, cellFilter.contextField).length > 0;
          } else if (contextObj && typeof contextObj === 'object') {
            const contextValue = contextObj[cellFilter.contextField];
            resolved = contextValue !== null && contextValue !== undefined;
          }
        }
        if (!resolved) {
          return true;
        }
      } else if (cellFilter.filterType === 'firstSelectedRow') {
        let resolved = false;
        if (cellFilter.sourceField && rows.length > 0) {
          resolved = this.collectFieldValues(rows, cellFilter.sourceField).length > 0;
        }
        if (!resolved) {
          return true;
        }
      }
    }

    return false;
  }
}
